import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import {
  VehicleListItem,
  vehicleListQuery,
} from "@/app/Vehicles/GraphQL/vehicleListQuery";
import { ReactNode } from "react";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { useVehicleActions } from "@/app/Vehicles/Hooks/useVehicleActions";
import type { TableId } from "@/app/Common/Utils/table-id";

import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import {
  QueryVehiclesOrderByColumn,
  SortOrder,
  VehicleListQueryVariables,
} from "@/gql/graphql";
import { useTranslations } from "use-intl";
import { CellContext } from "@/lib/Components/DataTable/types";
import { VehicleIcon } from "@/app/Vehicles/Components/VehicleIcon";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { PencilIcon } from "@heroicons/react/24/outline";
import { VehicleImageDialog } from "@/app/Vehicles/Components/VehicleImageDialog";
import { useNavigate } from "@tanstack/react-router";

export type VehicleColumns =
  | "image"
  | "name"
  | "code"
  | "extra_day_rate"
  | "actions"
  | "unit"
  | "transmission"
  | "type"
  | "amenities";

export function VehicleTable({
  id,
  rightButtons,
  columns,
  queryVariables,
}: {
  id: TableId;
  columns?: DataTableColDef<VehicleListItem, any, any>[];
  rightButtons?: ReactNode;
  queryVariables?: Partial<VehicleListQueryVariables>;
}) {
  const getActions = useVehicleActions();
  const { supplierId } = useGlobalSupplier();
  const t = useTranslations("vehicle");
  const tTable = useTranslations("vehicle.table");
  const defaultColumns: DataTableColDef<
    VehicleListItem,
    any,
    VehicleColumns
  >[] = [
    {
      id: "image",
      header: tTable("image"),
      cell: VehicleImageCell,
    },
    {
      id: "name",
      accessorFn: (data) => data.name,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("name")} />
      ),
      cell: ({ row }) => {
        return <VehicleNameCell vehicle={row.original} />;
      },
    },
    {
      id: "code",
      accessorFn: (data) => data.code,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("code")} />
      ),
    },
    {
      id: "unit",
      accessorFn: (data) => t(`hire_unit_type.${data.hire_unit_type}`),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("unit")} />
      ),
    },
    {
      id: "extra_day_rate",
      accessorFn: (data) =>
        formatCurrency(data.extra_hire_unit_rate, data.supplier.currency),
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={tTable("extra_day_rate")}
        />
      ),
    },
    {
      id: "type",
      accessorFn: (data) => t(`type.${data.type}`),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("type")} />
      ),
    },
    {
      id: "transmission",
      accessorFn: (data) => t(`transmission.${data.transmission}`),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("transmission")} />
      ),
    },
  ];

  return (
    <DataTable
      rightButtons={rightButtons}
      columns={columns ? columns : defaultColumns}
      document={vehicleListQuery}
      getActions={getActions}
      getQueryVariables={({ search, sorting, pagination }) => {
        const baseParams: VehicleListQueryVariables = {
          search,
          first: pagination.pageSize,
          page: pagination.pageIndex,
          supplier_id: supplierId ?? undefined,
          archived: false,
          ...queryVariables,
        };

        if (search) {
          return baseParams;
        }

        return {
          ...baseParams,
          orderBy: sorting.map((sort) => {
            const order = sort.desc ? SortOrder.Desc : SortOrder.Asc;

            return {
              column: getColumnSort(sort.id),
              order,
            };
          }),
        };
      }}
      accessor={(data) => data.vehicles}
      id={id}
      initialSorting={[{ id: "name", desc: false }]}
    />
  );
}

function getColumnSort(id: VehicleColumns): QueryVehiclesOrderByColumn {
  if (id === "name") {
    return QueryVehiclesOrderByColumn.Name;
  }

  if (id === "code") {
    return QueryVehiclesOrderByColumn.Code;
  }

  if (id === "extra_day_rate") {
    return QueryVehiclesOrderByColumn.ExtraHireUnitRate;
  }

  if (id === "type") {
    return QueryVehiclesOrderByColumn.Type;
  }

  if (id === "transmission") {
    return QueryVehiclesOrderByColumn.Transmission;
  }

  if (id === "unit") {
    return QueryVehiclesOrderByColumn.HireUnitType;
  }

  throw new Error(`Unknown sort column: ${id}`);
}

export function VehicleImageCell({ row }: CellContext<VehicleListItem, any>) {
  const vehicle = row.original;
  const { open } = useDialog(VehicleImageDialog);

  return (
    <button
      onClick={() => {
        open({
          vehicleId: vehicle.id,
        });
      }}
      className="relative"
    >
      <VehicleIcon className="size-10 rounded-sm" model={vehicle} />
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-full bg-gray-700/50 opacity-0 hover:opacity-100">
        <PencilIcon className="size-4 text-white" />
      </div>
    </button>
  );
}

function VehicleNameCell({ vehicle }: { vehicle: VehicleListItem }) {
  const navigate = useNavigate();

  function handleClick() {
    return navigate({
      to: ".",
      search: (prev: any) => ({
        ...prev,
        vehicleId: vehicle.id,
      }),
    });
  }

  return (
    <button
      onClick={handleClick}
      className="text-left font-medium text-indigo-600 hover:text-indigo-900"
    >
      {vehicle.name}
    </button>
  );
}
